
import { useState, useEffect } from "react";
import { useAuth } from "@/components/AuthProvider";
import { supabase } from "@/integrations/supabase/client";

export type AdminRole = 'super_admin' | 'admin' | 'content_manager';

export interface AdminStatus {
  isAdmin: boolean;
  role: AdminRole | null;
  canAccessCMS: boolean;
  canManageAdmins: boolean;
}

export function useAdminStatus() {
  const [adminStatus, setAdminStatus] = useState<AdminStatus>({
    isAdmin: false,
    role: null,
    canAccessCMS: false,
    canManageAdmins: false
  });
  const { session } = useAuth();

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!session?.user) {
        setAdminStatus({
          isAdmin: false,
          role: null,
          canAccessCMS: false,
          canManageAdmins: false
        });
        return;
      }

      try {
        const { data } = await supabase
          .from('admin_users')
          .select('id, role')
          .eq('user_id', session.user.id)
          .maybeSingle();

        if (data) {
          const role = data.role as AdminRole;
          setAdminStatus({
            isAdmin: true,
            role,
            canAccessCMS: ['super_admin', 'admin', 'content_manager'].includes(role),
            canManageAdmins: ['super_admin'].includes(role)
          });
        } else {
          setAdminStatus({
            isAdmin: false,
            role: null,
            canAccessCMS: false,
            canManageAdmins: false
          });
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        setAdminStatus({
          isAdmin: false,
          role: null,
          canAccessCMS: false,
          canManageAdmins: false
        });
      }
    };

    checkAdminStatus();
  }, [session]);

  return adminStatus;
}
